/**
 * Plan-spine step names and router-state shapes — shared (client-safe).
 *
 * The Plan agent runs as five named steps in fixed first-pass order. The
 * server-side router (src/lib/server/nova/agents/plan/router.ts) owns the
 * step registry, order gating, and resolution; this module holds only the
 * boundary types the client (welcome starters, room chips) and the server
 * both need.
 *
 * Aliases: plan steps, plan spine steps, plan router types, step union
 */

import { z } from "zod";

import { READINESS_SCORE_NAME } from "~/lib/shared/readiness/config";

/** The five Plan steps, in fixed first-pass order. */
export const PLAN_STEP_NAMES = [
  "getting_to_know_you",
  "identity_architect",
  "icp_definition",
  "market_check",
  "core_offer_design",
] as const;

export type PlanStepName = (typeof PLAN_STEP_NAMES)[number];

export const planStepNameSchema = z.enum(PLAN_STEP_NAMES);

/**
 * One-line, plain-language description of what each Plan step is FOR —
 * coach-facing copy for the in-room path (the step rail) and the welcome
 * header. Deliberately jargon-free: "Who exactly you're for", never "ICP
 * definition" (that stays the internal label). Keyed for every step so the
 * map is exhaustive.
 *
 * Aliases: plan step descriptions, plan path copy, step subtitles
 */
export const PLAN_STEP_DESCRIPTIONS: Record<PlanStepName, string> = {
  getting_to_know_you: "Who you are and where you stand",
  identity_architect: "The coach you're becoming",
  icp_definition: "Who exactly you're for",
  market_check: "Will they pay — and why you",
  core_offer_design: "What exactly you sell",
};

/**
 * The message a coach's client sends when they tap a COMPLETED step on the
 * path to revisit it. The Plan head is one persistent conversation, so a
 * revisit is a natural-language nudge that the plan agent answers by calling
 * `enter_plan_step` for that step (the order gate always lets a completed
 * step back in — a free revisit). Client-safe so the room can hand it to the
 * chat's send path via the `revisitStep` route search param.
 *
 * Aliases: plan step revisit prompts, revisit starter, step re-entry message
 */
export const PLAN_STEP_REVISIT_PROMPTS: Record<PlanStepName, string> = {
  getting_to_know_you: "Let's revisit who I am and where I stand.",
  identity_architect: "Let's revisit the coach I'm becoming.",
  icp_definition:
    "Let's revisit who exactly I'm for — I want to refine my ideal client.",
  market_check:
    "Let's revisit the market check — I want to re-test my ideal client against the market.",
  core_offer_design:
    "Let's revisit my core offer — I want to sharpen what I sell.",
};

/**
 * The opening message auto-sent when a coach STARTS (not revisits) a Plan step
 * from a room CTA or the path's current-step button — the "kickoff" that spares
 * them clicking a welcome starter (founder ask, PR #967). One per step so the
 * kickoff matches wherever the router drops them; the plan agent answers by
 * entering that step. Distinct from `PLAN_STEP_REVISIT_PROMPTS` (re-entering a
 * COMPLETED step): a kickoff opens the current step, a revisit re-opens a done
 * one. Client-safe — resolved to canned copy on the client, never passed as
 * free text through the URL.
 *
 * Aliases: plan step kickoff prompts, plan step opening message, auto-send plan
 */
export const PLAN_STEP_KICKOFF_PROMPTS: Record<PlanStepName, string> = {
  getting_to_know_you: "Let's get to know each other.",
  identity_architect: "Let's define the coach I'm becoming.",
  icp_definition: "Let's define exactly who I'm for.",
  market_check: "Let's pressure-test my ideal client against the market.",
  core_offer_design: "Let's shape my core offer.",
};

/**
 * Kickoff openings for the two single-step foundation agents (Diagnose, Grow) —
 * the agent-level equivalent of `PLAN_STEP_KICKOFF_PROMPTS`, auto-sent when a
 * coach starts one of those sessions from a room CTA or the studio door. The
 * score is taken on its own page, so the Diagnose kickoff asks about the
 * result; without one, Diagnose sends the coach to take it.
 *
 * Aliases: diagnose kickoff prompt, grow kickoff prompt, foundation kickoff
 */
export const DIAGNOSE_KICKOFF_PROMPT = `Walk me through my ${READINESS_SCORE_NAME}.`;
export const GROW_KICKOFF_PROMPT = "Let's build my 90-day plan.";

/**
 * Conversation `extractedData` key holding the router state (active step +
 * recap). Owned exclusively by the `enter_plan_step` tool — listed in
 * save-extracted-data's PROTECTED_KEYS (so a model save can't drop it) and
 * the agents' INTERNAL_EXTRACTED_DATA_KEYS (so it stays out of model
 * context), mirroring `niche_verdict`. Step COMPLETION is never read from
 * this record — completion is always derived coach-scoped from durable
 * artifacts (getStepDerivationForCoach).
 */
export const PLAN_ROUTER_KEY = "plan_router";

/**
 * Conversation `metadata` key stamped `true` on every plan head created since
 * the spine cutover (see `findOrCreatePlanHead`). Its ABSENCE marks a
 * pre-spine head: a terminal pre-spine head is a finished workshop and is
 * never reopened, while a terminal stamped head was only swept and reopens
 * for revisits.
 */
export const PLAN_SPINE_HEAD_KEY = "plan_spine";

/** Router state persisted under PLAN_ROUTER_KEY. */
export const planRouterRecordSchema = z.object({
  activeStep: planStepNameSchema,
  /** One-line, model-authored recap of where the step's work stands. */
  recap: z.string(),
  enteredAt: z.string(),
  /**
   * True when the coach entered `activeStep` as a DELIBERATE revisit — the
   * step was already durably complete at entry time (`enter_plan_step`'s
   * decision.revisit). This is what tells resume resolution apart:
   *   - a completed step with `revisit: true` was chosen on purpose → honored;
   *   - a completed step with `revisit: false` was being WORKED when it
   *     completed under the session → a stale pointer, dropped in favour of the
   *     derived current step (never silently re-opened, per #967).
   * Defaults to false so pre-#967 records (no flag) read as stale-droppable,
   * self-healing on the next `enter_plan_step`.
   */
  revisit: z.boolean().default(false),
});

export type PlanRouterRecord = z.infer<typeof planRouterRecordSchema>;

/** Per-step progress line for the roadmap header and room chips. */
export interface PlanStepProgress {
  name: PlanStepName;
  label: string;
  complete: boolean;
  /**
   * True only on the market-check entry when its verdict is bound to a
   * superseded ICP revision (`icpVerdict.status === "stale"`) — the amber
   * "re-run the market check" chip state.
   */
  verdictStale: boolean;
  /**
   * True when the order gate (`canEnterPlanStep`) would ADMIT this step given
   * the coach's durable artifacts — the enablement exemption and the step-5
   * verdict gate included. Carries true enterability into the client so the room
   * path locks a step ONLY when the server would actually refuse it, never by
   * positional inference (a grandfathered coach's core-offer step is enterable
   * even with an earlier exempt gap still open). Complete and current steps are
   * always enterable; it's the not-started steps this disambiguates.
   */
  enterable: boolean;
}

/**
 * The router's answer to "where is this coach in Plan right now" — resolved
 * per turn from the conversation's `plan_router` record when present and
 * still enterable, otherwise derived from coach-scoped artifacts.
 */
export interface ResolvedPlanStep {
  step: PlanStepName;
  /** One-line resume recap (stored, or derived from artifact state). */
  recap: string;
  source: "router_record" | "derived";
  /** True when the resolved step is already complete (a free revisit). */
  revisit: boolean;
  /** Enabled steps in first-pass order with completion state. */
  progress: PlanStepProgress[];
  /**
   * Plan-completion state per the central predicate (`isPlanComplete`):
   * a `core_offer` exists.
   * Drives the session hand-off banner and the room card's complete state —
   * NEVER conversation-terminal status (plan heads stay live for revisits).
   */
  planComplete: boolean;
}

// ---------------------------------------------------------------------------
// Room-card chips — discrete per-step states (R5)
// ---------------------------------------------------------------------------

/**
 * Discrete chip state for a Plan step — never a percentage. Only
 * enabled steps ever appear (disabled slots render nothing, R4).
 */
export type PlanStepChipState =
  | "not_started"
  | "current"
  | "complete"
  | "verdict_stale";

export interface PlanStepChip {
  name: PlanStepName;
  label: string;
  state: PlanStepChipState;
  /**
   * The order gate's verdict for this step (see `PlanStepProgress.enterable`).
   * The in-room path renders a not-started step as LOCKED only when this is
   * false; an enterable not-started step gets a Start affordance. The compact
   * studio-home card ignores it.
   */
  enterable: boolean;
}

/**
 * Map a resolved step + its progress lines onto the room card's chip states.
 * Precedence per step: a stale verdict outranks everything (the coach must
 * re-run the check whether or not they are parked on it), then durable
 * completion, then the resolved current step, else not started. `enterable`
 * carries through from progress untouched. Pure and shared so the server
 * function and tests use the identical mapping.
 *
 * Aliases: plan step chips, room card step states, derive plan chips
 */
export function derivePlanStepChips(
  resolved: Pick<ResolvedPlanStep, "step" | "progress">,
): PlanStepChip[] {
  return resolved.progress.map((p) => ({
    name: p.name,
    label: p.label,
    enterable: p.enterable,
    state: p.verdictStale
      ? "verdict_stale"
      : p.complete
        ? "complete"
        : p.name === resolved.step
          ? "current"
          : "not_started",
  }));
}
